import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import { useBoms, useCreateBom, useDeleteBom } from '@/lib/boms';

export function BomsPage() {
  const me = useMe();
  const orgSlug = useActiveOrgSlug(me.data?.organizations);
  const list = useBoms(orgSlug);
  const create = useCreateBom(orgSlug);
  const remove = useDeleteBom(orgSlug);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Two submit paths share the same form: "Create BOM" stays on this
  // page (the user populates lines manually), "Create & import" routes
  // straight into the BOM-import wizard with the new draft revision
  // pre-selected. The single shared form keeps the UI tight without
  // duplicating name/description inputs in a modal.
  const submitCreate = async (gotoImport: boolean) => {
    if (!name.trim()) return;
    try {
      const bom = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
      });
      setName('');
      setDescription('');
      if (gotoImport) {
        navigate(`/boms/${bom.id}/import`);
      }
    } catch {
      /* surfaced below */
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitCreate(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">BOMs</h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          Bills of materials. Each BOM has revisions you can freeze and an
          availability check that compares lines against your inventory.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="flex-wrap items-end gap-2 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4"
      >
        <label className="flex-1 min-w-[200px]">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">Name</span>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Synth v1 main board"
          />
        </label>
        <label className="flex-1 min-w-[240px]">
          <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
            Description (optional)
          </span>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          {create.isPending ? 'Creating…' : 'Create BOM'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => submitCreate(true)}
          disabled={create.isPending || !name.trim()}
        >
          Create & import…
        </Button>
        {create.isError && (
          <p className="basis-full text-sm text-red-600">
            {(create.error as Error).message}
          </p>
        )}
      </form>

      <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
        <table className="w-full text-left text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Latest revision</th>
              <th className="px-3 py-2 text-right">Lines</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.isPending && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-stone-400 dark:text-stone-500">
                  Loading…
                </td>
              </tr>
            )}
            {list.data && list.data.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-stone-500 dark:text-stone-400">
                  No BOMs yet — create your first one above.
                </td>
              </tr>
            )}
            {list.data?.map((bom) => (
              <tr key={bom.id} className="border-t border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800">
                <td className="px-3 py-2">
                  <Link
                    to={`/boms/${bom.id}`}
                    className="font-medium text-forge-700 dark:text-forge-400 hover:underline"
                  >
                    {bom.name}
                  </Link>
                  {bom.description && (
                    <div className="mt-0.5 text-xs text-stone-500 dark:text-stone-400">{bom.description}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {bom.latest_revision ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="font-mono">Rev {bom.latest_revision.revision_label}</span>
                      {bom.latest_revision.frozen_at ? (
                        <span className="rounded bg-stone-200 dark:bg-stone-700 px-1.5 py-0.5 text-xs">
                          frozen
                        </span>
                      ) : (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          draft
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-stone-400 dark:text-stone-500">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {bom.latest_revision?.line_count ?? 0}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {bom.latest_revision &&
                      bom.latest_revision.frozen_at === null && (
                        <Link
                          to={`/boms/${bom.id}/import`}
                          className="text-xs text-forge-600 hover:text-forge-800 dark:hover:text-forge-300"
                        >
                          Import
                        </Link>
                      )}
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Delete "${bom.name}"? This is a soft delete.`)) {
                          remove.mutate(bom.id);
                        }
                      }}
                      className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
